import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CREATE_PHOTO_ALBUM, UPDATE_PHOTO_ALBUM } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoAlbum } from '../types'

interface Props {
  projectId: string
  /** The album to rename; null/undefined creates a new one. */
  album?: PhotoAlbum | null
  open: boolean
  onClose: () => void
}

const AlbumDialog: React.FC<Props> = ({ projectId, album, open, onClose }) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [create, { loading: creating }] = useMutation(CREATE_PHOTO_ALBUM, { context: { handlesErrors: true }, refetchQueries: ['photoAlbums'] })
  const [update, { loading: updating }] = useMutation(UPDATE_PHOTO_ALBUM, { context: { handlesErrors: true }, refetchQueries: ['photoAlbums'] })
  const saving = creating || updating

  useEffect(() => {
    if (open) {
      setName(album?.name ?? '')
      setError(null)
    }
  }, [open, album?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = name.trim()
  const valid = trimmed.length >= 1 && trimmed.length <= 100

  const submit = async () => {
    if (!valid || saving) return
    try {
      if (album) await update({ variables: { id: album.id, name: trimmed } })
      else await create({ variables: { projectId, name: trimmed } })
      onClose()
    } catch (e: any) {
      setError(describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{album ? t('Photos_album_rename') : t('Photos_album_new')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="photo-album-name">{t('Photos_album_name')}</Label>
          <Input id="photo-album-name" autoFocus maxLength={100} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {album ? t('Common_save') : t('Common_create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AlbumDialog
